import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { NAV, AUTH } from "@/constants/testIds";
import ConfigHealthRibbon from "@/components/app/ConfigHealthRibbon";
import TwilioA2PReminder from "@/components/app/TwilioA2PReminder";
import {
  LayoutDashboard,
  Search,
  KanbanSquare,
  CreditCard,
  LogOut,
  Bot,
  Calendar,
  MessageSquare,
  MessageSquareText,
  Users,
  Settings,
  BookOpen,
  Briefcase,
  FileText,
  Megaphone,
  ExternalLink,
  Inbox,
  TrendingUp,
  Zap,
  BarChart3,
  Globe,
  Sparkles,
  Phone,
  Clock,
  ShieldCheck,
  ClipboardList,
  HardDrive,
} from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: NAV.dashboard, end: true, section: "console" },
  { to: "/discover", label: "Discover leads", icon: Search, testId: NAV.discover, section: "console" },
  { to: "/discover/apollo", label: "Apollo B2B search", icon: Zap, testId: "nav-apollo-search-link", section: "console" },
  { to: "/leads", label: "Leads", icon: KanbanSquare, testId: NAV.leads, section: "console" },
  { to: "/inbound", label: "Inbound", icon: Inbox, testId: NAV.publicLeads, section: "console" },
  { to: "/outreach", label: "AI Outreach", icon: Phone, testId: "nav-outreach-link", section: "console" },
  { to: "/watchlists", label: "Watchlists", icon: Clock, testId: "nav-watchlists-link", section: "console" },
  { to: "/clients", label: "Clients", icon: Briefcase, testId: NAV.clients, section: "crm", denyMarketing: true },
  { to: "/onboarding", label: "Onboarding", icon: FileText, testId: NAV.onboarding, section: "crm", denyMarketing: true },
  { to: "/marketing", label: "Marketing", icon: Megaphone, testId: NAV.marketing, section: "crm" },
  { to: "/addons", label: "Add-Ons", icon: Sparkles, testId: "nav-addons-link", section: "crm", denyMarketing: true },
  { to: "/ai-visibility-hub", label: "AI Visibility Hub", icon: Sparkles, testId: "nav-ai-visibility-hub-link", section: "crm" },
  { to: "/audit-console", label: "AI Audit Console", icon: Sparkles, testId: "nav-audit-console-link", section: "crm" },
  { to: "/review-templates", label: "Review Templates", icon: Sparkles, testId: "nav-review-templates-link", section: "crm" },
  { to: "/settings/missed-call", label: "Missed-Call Text-Back", icon: Sparkles, testId: "nav-missed-call-link", section: "backoffice", denyMarketing: true },
  { to: "/reactivation", label: "DB Reactivation", icon: Users, testId: "nav-reactivation-link", section: "crm" },
  { to: "/reactivation/intakes", label: "Reactivation Intakes", icon: Users, testId: "nav-reactivation-intakes-link", section: "crm" },
  { to: "/reactivation/needs-human", label: "Human Follow-ups", icon: Users, testId: "nav-reactivation-needs-human-link", section: "crm" },
  { to: "/reactivation/digests", label: "  ↳ Digest history", icon: Megaphone, testId: "nav-reactivation-digests-link", section: "crm" },
  { to: "/intakes", label: "  ↳ Intakes", icon: ClipboardList, testId: "nav-intakes-link", section: "crm" },
  { to: "/files", label: "Files & media", icon: HardDrive, testId: "nav-files-link", section: "crm" },
  { to: "/contracts", label: "Contracts", icon: FileText, testId: "nav-contracts-link", section: "crm", denyMarketing: true },
  { to: "/contractors", label: "  ↳ Contractors", icon: Users, testId: "nav-contractors-link", section: "crm", denyMarketing: true },
  { to: "/service-packages", label: "  ↳ Pricing", icon: CreditCard, testId: "nav-packages-link", section: "crm", denyMarketing: true },
  { to: "/jobs", label: "  ↳ Jobs", icon: Briefcase, testId: "nav-jobs-link", section: "crm", denyMarketing: true },
  { to: "/payouts", label: "  ↳ Payouts", icon: CreditCard, testId: "nav-payouts-link", section: "crm", denyMarketing: true },
  { to: "/portal-analytics", label: "Portal analytics", icon: BarChart3, testId: "nav-portal-analytics-link", section: "crm" },
  { to: "/agents", label: "AI agents", icon: Bot, testId: NAV.agents, section: "agents" },
  { to: "/widget-chats", label: "Widget chats", icon: MessageSquareText, testId: "nav-widget-chats-link", section: "agents" },
  { to: "/roi-signals", label: "ROI signals", icon: TrendingUp, testId: "nav-roi-signals-link", section: "agents" },
  { to: "/sequences", label: "Sequences", icon: Zap, testId: "nav-sequences-link", section: "agents" },
  { to: "/bookings", label: "Bookings", icon: Calendar, testId: NAV.bookings, section: "agents" },
  { to: "/calendar", label: "Calendar", icon: Calendar, testId: "nav-calendar-link", section: "agents" },
  { to: "/conversations", label: "Conversations", icon: MessageSquare, testId: NAV.conversations, section: "agents" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports-link", section: "backoffice" },
  { to: "/domains", label: "Domains", icon: Globe, testId: "nav-domains-link", section: "backoffice", denyMarketing: true },
  { to: "/subscribers", label: "Subscribers", icon: Users, testId: NAV.subscribers, section: "backoffice", denyMarketing: true },
  { to: "/users", label: "System users", icon: ShieldCheck, testId: "nav-users-link", section: "backoffice", denyMarketing: true },
  { to: "/industries-admin", label: "Industries", icon: Briefcase, testId: "nav-industries-admin-link", section: "backoffice", denyMarketing: true },
  { to: "/settings/webhook", label: "Webhook Intake", icon: Briefcase, testId: "nav-webhook-settings-link", section: "backoffice", denyMarketing: true },
  { to: "/settings/resend",  label: "Resend Config",  icon: Briefcase, testId: "nav-resend-settings-link",  section: "backoffice", denyMarketing: true },
  { to: "/settings/twilio",  label: "Twilio Config",  icon: Briefcase, testId: "nav-twilio-settings-link",  section: "backoffice", denyMarketing: true },
  { to: "/settings/apollo",  label: "Apollo Config",  icon: Zap,       testId: "nav-apollo-settings-link",  section: "backoffice", denyMarketing: true },
  { to: "/settings/api-tokens", label: "Partner API tokens", icon: ShieldCheck, testId: "nav-api-tokens-link", section: "backoffice", denyMarketing: true },
  { to: "/settings/audit-console", label: "Audit Console key", icon: Sparkles, testId: "nav-audit-console-config-link", section: "backoffice", denyMarketing: true },
  { to: "/security", label: "Security · 2FA", icon: ShieldCheck, testId: "nav-security-link", section: "backoffice" },
  { to: "/billing", label: "Billing", icon: CreditCard, testId: NAV.billing, section: "backoffice", denyMarketing: true },
  { to: "/settings", label: "Settings", icon: Settings, testId: NAV.settings, section: "backoffice", denyMarketing: true },
  { to: "/help", label: "Help & how-to", icon: BookOpen, testId: "nav-help-link", section: "backoffice" },
];

const SECTIONS = [
  { key: "console", label: "Lead engine" },
  { key: "crm", label: "CRM & growth" },
  { key: "agents", label: "AI product" },
  { key: "backoffice", label: "Back office" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const current = navItems.find((n) => location.pathname === n.to || (n.to !== "/dashboard" && location.pathname.startsWith(n.to)));
    document.title = current ? `${current.label} · ClientsOnramp Console` : "ClientsOnramp Console";
    return () => { document.title = "ClientsOnramp — Your business never misses another call."; };
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  const isMarketing = (user?.role || "") === "marketing";
  const visibleNav = isMarketing ? navItems.filter((n) => !n.denyMarketing) : navItems;
  return (
    <div className="flex min-h-screen">
      <aside className="lf-sidebar w-64 flex flex-col justify-between p-6">
        <div>
          <Link
            to="/dashboard"
            data-testid={NAV.brand}
            className="flex items-center mb-8 no-underline"
          >
            <img
              src="/logo-on-dark.png"
              alt="Clients OnRamp"
              style={{ height: 40, width: "auto", display: "block", objectFit: "contain" }}
            />
          </Link>
          <Link
            to="/"
            className="text-xs text-white/50 hover:text-white mb-6 inline-flex items-center gap-1 transition"
          >
            <ExternalLink className="w-3 h-3" /> Public site
          </Link>
          {SECTIONS.map((section) => {
            const items = visibleNav.filter((n) => n.section === section.key);
            if (items.length === 0) return null;
            return (
            <div key={section.key} className="mb-6">
              <div className="overline mb-3 opacity-70">{section.label}</div>
              <nav className="flex flex-col gap-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-l-2 border-transparent ${
                        isActive ? "active" : ""
                      }`
                    }
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
            );
          })}
        </div>
        <div>
          <div className="overline mb-3 opacity-70">Signed in</div>
          <div className="text-sm mb-4">
            <div className="text-white font-medium">{user?.name}</div>
            <div className="text-xs opacity-60 font-mono">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid={AUTH.logoutBtn}
            className="flex items-center gap-2 text-sm font-medium hover:text-white transition mb-6"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
          <div className="pt-4 border-t border-white/10 flex items-center gap-3 text-xs opacity-60">
            <a href="/privacy" target="_blank" rel="noreferrer" data-testid="nav-privacy-link" className="hover:text-white hover:opacity-100 transition">Privacy</a>
            <span>·</span>
            <a href="/terms" target="_blank" rel="noreferrer" data-testid="nav-terms-link" className="hover:text-white hover:opacity-100 transition">Terms</a>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 flex flex-col" style={{ background: "#F7F4EE" }}>
        <header
          className="sticky top-0 z-40 flex items-center justify-end gap-3 px-6 py-2.5 border-b border-neutral-200 backdrop-blur"
          style={{ background: "rgba(247,244,238,0.92)" }}
          data-testid="app-topbar"
        >
          <div className="hidden sm:flex flex-col items-end mr-1 leading-tight">
            <div className="text-xs font-medium text-neutral-900">{user?.name || user?.email}</div>
            <div className="text-[10px] font-mono text-neutral-500">{user?.role || "member"}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="topbar-logout-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white px-3 py-1.5 transition"
            title="Log out of the admin console"
          >
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </header>
        <div className="flex-1 min-w-0">
          <ConfigHealthRibbon />
          <TwilioA2PReminder />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
